import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { NbDialogRef, NbCardModule, NbButtonModule, NbSpinnerModule, NbSelectModule, NbOptionModule, NbCheckboxModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { combineLatest, first, Subscription } from 'rxjs';

import { FixedSizeVirtualScrollStrategy, VIRTUAL_SCROLL_STRATEGY, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { KPI } from '../../../@core/api';
import {
  DataType,
  FilterType,
  PerimeterFilter,
} from '../../../@core/perimeter-filter';
import { PerimetersService } from '../../../@core/utils';
import { UsernamePipe } from '../../pipes/username.pipe';
import { SelectAllComponent } from '../select-all/select-all.component';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { IS_FIRST_CONNECTION_STORAGE_KEY } from '../../../@core/utils/constants';
import { toSignal } from '@angular/core/rxjs-interop';


@Component({
  selector: 'app-perimeters-filters-dialog',
  styleUrls: ['./perimeters-filters-dialog.component.scss'],
  templateUrl: './perimeters-filters-dialog.component.html',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useClass: PerimetersFiltersDialogComponent,
    },
  ],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    NbSpinnerModule,
    FormsModule,
    NbSelectModule,
    NbOptionModule,
    SelectAllComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    NbCheckboxModule,
    TranslateModule,
    UsernamePipe,
    
  ],
})
export class PerimetersFiltersDialogComponent
  extends FixedSizeVirtualScrollStrategy
  implements OnInit, OnDestroy {
  // Used to force the user to choose at least one filter before leaving the dialog
  @Input() disableClosingDiablog: boolean;
  @Input() title: string = _('Select one or more sites');

  public selectedPerimeters: KPI.Perimeter[] = [];
  public filteredPerimeters: KPI.Perimeter[] = [];
  public allFilteredPerimeters: KPI.Perimeter[] = [];
  public filters: PerimeterFilter[] = [];
  public searchInput: string;
  public filterType: typeof FilterType = FilterType;
  public dataType: typeof DataType = DataType;
  public selectedPerimetersCount: string;
  public filtersLoading: Signal<boolean> = toSignal(this.perimetersService.filtersLoading$);
  public search: string = '';
  public activeFiltersNb: WritableSignal<number> = signal(0);
  public allowedPerimeters: Signal<KPI.Perimeter[]> = toSignal(this.perimetersService.allPerimeters$.pipe(first()));
  public supportEmail: string = 'support@kpi-intelligence.com';
  private subscriptions: Subscription[] = [];
  public selectedPerimetersMap: { [id: number]: boolean } = {};
  private hasChanged: boolean = false;

  constructor(
    private perimetersService: PerimetersService,
    private ref: NbDialogRef<PerimetersFiltersDialogComponent>,
    private translate: TranslateService,
  ) {
    super(30, 250, 500);
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([
        this.perimetersService.filteredPerimeters$,
        this.perimetersService.perimeters$
      ]).subscribe(([filteredPerimeters, perimeters]) => {
        this.selectedPerimeters = perimeters;
        // Enable the submit button if perimeters are already selected
        if (this.selectedPerimeters.length > 0) {
          this.disableClosingDiablog = false;
        }
        const isFirstConnection = localStorage.getItem(IS_FIRST_CONNECTION_STORAGE_KEY);
        if ((isFirstConnection !== null || isFirstConnection === 'false')) {
          this.perimetersService.updateSelectedPerimetersMap(
            filteredPerimeters,
            this.selectedPerimeters,
          );
        } else {
          // If it's the first connection, select all perimeters by default
          this.perimetersService.updateSelectedPerimetersMap(
            filteredPerimeters,
            filteredPerimeters,
          );
        }
        // Sort by perimeter name
        this.allFilteredPerimeters = filteredPerimeters.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
        this.filteredPerimeters = this.allFilteredPerimeters;
        this.updateSelectedPerimeters();
      }),
      this.perimetersService.filters$.subscribe((filters) => {
        const savedSelections = this.perimetersService.perimeterFilterSelection;
        this.filters = filters.map((filter) => {
          const savedSelection = savedSelections?.find((f) => f.label === filter.label);
          if (savedSelection) {
            filter.selectedValues = savedSelection.selectedValues;
            filter.selectedValueRange = savedSelection.selectedValueRange;
          }
          return filter;
        });
        this.calculateFilters();
      }),
      this.perimetersService.selectedPerimetersMap$.subscribe((selectedPerimetersMap) => {
        this.selectedPerimetersMap = selectedPerimetersMap;
        this.updateSelectedPerimeters();
      }),
    );

    this.perimetersService.filterPerimeters(this.searchInput);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.hasChanged) {
      window.location.reload();
    }
  }

  public clearFilters(): void {
    this.filters.map((filter) => filter.clearSelected());
    this.updateFilters();
    this.search = '';
    this.toggleAll(true);
  }

  public updateFilters(): void {
    this.perimetersService.filterPerimeters(this.searchInput);
    if (this.filters.every((filter) => filter.selectedValues.length === 0)) {
      this.toggleAll(true);
    }
    this.perimetersService.savePerimeterFilterSelection(this.filters);
    this.calculateFilters();
  }

  public toggleAll(selected: boolean): void {
    this.perimetersService.toggleAllPerimeters(this.filteredPerimeters, selected);
  }

  public onPerimeterSelectionChange(perimeterId: number, selected: boolean): void {
    this.perimetersService.setPerimeterSelection(perimeterId, selected);
    this.hasChanged = true;
  }

  private getSelectedPerimeters(): KPI.Perimeter[] {
    const selectedPerimeters: KPI.Perimeter[] = this.allFilteredPerimeters?.reduce((acc, p) => {
      if (this.selectedPerimetersMap[p.id]) {
        acc.push(p);
      }
      return acc;
    }, []);
    return selectedPerimeters;
  }

  public updateSelectedPerimeters(): void {
    this.selectedPerimeters = this.getSelectedPerimeters();
    // Disable submit button if no perimeter is selected
    this.disableClosingDiablog =
      this.selectedPerimeters.length > 0 ? false : true;
    this.translate
      .get('theme.perimeters-filters-dialog.selected_perimeters_count', {
        number: this.selectedPerimeters.length,
      })
      .subscribe(
        (selectedPerimetersCount) =>
          (this.selectedPerimetersCount = selectedPerimetersCount),
      );
  }



  public searchSite(event) {
    if (event.target.value?.length) {
      this.filteredPerimeters = this.allFilteredPerimeters.filter((site) =>
        this.formateString(site.name).includes(this.formateString(event.target.value))
      );
    } else {
      this.filteredPerimeters = this.allFilteredPerimeters;
    }
    this.calculateFilters();
  }

  public formateString(string: string) {
    return string
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private calculateFilters(): void {
    this.activeFiltersNb.set(
      this.filters.filter((filter) =>
        filter.selectedValues.length !== 0 || filter.selectedValueRange
      ).length + (this.search !== '' ? 1 : 0)
    );
  }

  public submit(): void {
    const selectedPerimetersIds = [];
    this.selectedPerimeters.map((p) => {
      selectedPerimetersIds.push(p.id);
      selectedPerimetersIds.push(...p.sub_perimeters.map((sp) => sp.id));
    });
    this.perimetersService.updateSelectedPerimeters(this.selectedPerimeters);
    localStorage.setItem(IS_FIRST_CONNECTION_STORAGE_KEY, 'false');
    this.ref.close();
  }
}
